import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import moment from "moment";

import { mahbLogo } from "./mahbLogo";

pdfMake.vfs = pdfFonts.pdfMake.vfs;

/* Wording after the MAHB sample work order ("Sample work order 3.pdf"); colours
   and layout after the design: navy bars, a pink title, navy dashed rules. */
const NAVY = "#29256E";
const TITLE = "#E83E5C";
const STRIPE = "#F3F2FC";
const LINE = "#C9C6F2";
const WHITE = "#FFFFFF";
/* A4 portrait less 36pt margins each side. */
const WIDTH = 523;

const ADDRESS = [
  "Malaysia Airports Holding Berhad.",
  "Malaysia Airports Corporate Office,",
  "Persiaran Korporate KLIA,",
  "64000 KLIA, Sepang,",
  "Selangor Darul Ehsan",
];

const money = (value: number) =>
  value.toLocaleString("en-MY", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
/* SOR figures read as entered: thousands separated, no forced decimals. */
const figure = (value: any) =>
  value === undefined || value === null || value === "" || isNaN(Number(value))
    ? String(value ?? "")
    : Number(value).toLocaleString("en-MY", { maximumFractionDigits: 2 });

const pick = (...values: any[]) => values.find((value) => value !== undefined && value !== null && value !== "");

/* "25 Sept 2026", as the sample writes the date. */
const issueDate = (value?: string) =>
  value && moment(value).isValid() ? moment(value).format("D MMM YYYY").replace(/ Sep /, " Sept ") : "-";

/** The navy dashed rule between sections. */
const rule = () => ({
  canvas: [
    { type: "line", x1: 0, y1: 0, x2: WIDTH, y2: 0, lineWidth: 1, lineColor: NAVY, dash: { length: 4, space: 3 } },
  ],
  margin: [0, 4, 0, 6],
});

/** A full-width navy bar heading a section. */
const bar = (text: string) => ({
  table: { widths: ["*"], body: [[{ text, bold: true, color: WHITE, fillColor: NAVY, margin: [6, 4, 6, 4] }]] },
  layout: "noBorders",
});

const tick = (size = 10) => ({
  canvas: [
    {
      type: "polyline",
      lineWidth: size < 9 ? 1 : 1.6,
      lineColor: NAVY,
      points: [
        { x: 0, y: size * 0.55 },
        { x: size * 0.35, y: size },
        { x: size, y: 0 },
      ],
    },
  ],
});

/** Calendar months from commencement to completion; days when under a month. */
const durationOf = (from?: string, to?: string) => {
  if (!from || !to || !moment(from).isValid() || !moment(to).isValid()) return "-";
  const months = moment(to).diff(moment(from), "months");
  if (months >= 1) return `${months} Month${months > 1 ? "s" : ""}`;
  const days = moment(to).diff(moment(from), "days");
  return `${days} Day${days === 1 ? "" : "s"}`;
};

/** A dotted rule a signature or a stamp goes on. */
const dotted = (width: number) => ({
  canvas: [
    { type: "line", x1: 0, y1: 0, x2: width, y2: 0, dash: { length: 2, space: 2 }, lineWidth: 0.8, lineColor: NAVY },
  ],
});

/**
 * The work order as the letter sent to the vendor — "Draft Work Order", and
 * the same document titled "Work Order" once approved (Final Work Order).
 *
 * Every page: MAHB logo and address, the title, a rule. Page 1: To / Work Order
 * Number / Date of Issuance / Job Title, the Work Order Information rows, the
 * attachment checklist and the acceptance block. Page 2 on: the SOR schedule
 * ending in the total (inclusive of SST). Sum and performance bond (5%) are
 * worked from the SOR total; Insurances, LAD and DLP are left blank.
 */
export const draftWorkOrderPdf = (detail: any, title = "Draft Work Order") => {
  const sor: any[] = detail?.sorDetails ?? detail?.sorItems ?? [];
  const total = Number(detail?.totalAmount ?? sor.reduce((sum, row) => sum + (Number(row.amount) || 0), 0)) || 0;
  const jobTitle = pick(detail?.jobTitle, detail?.projectTitle) ?? "-";
  const workOrderNo = detail?.workOrderNo ?? "-";

  /* "Label : value" — the value in navy bold italics. */
  const labelled = (label: string, value: any, colSpan = 1) => [
    { text: label, margin: [0, 2, 0, 2] },
    { text: ":", margin: [0, 2, 0, 2] },
    { text: String(value ?? "-"), color: NAVY, bold: true, italics: true, fontSize: 9.5, colSpan, margin: [0, 1.5, 0, 2] },
  ];

  /* `blankLines`: room left to write in by hand, about one line (10pt) each. */
  const info = (number: number, label: string, value: string, blankLines = 0) => [
    { text: `${number}. ${label}`, margin: [0, 2, 0, 2] },
    { text: ":", margin: [0, 2, 0, 2] },
    /* "" is a line left blank on purpose (7–9); a missing value reads "-". */
    {
      text: value === "" ? "" : String(value || "-").toUpperCase(),
      italics: true,
      fontSize: 8,
      margin: [0, 2.5, 0, 2 + blankLines * 10],
    },
  ];

  /* The attachment checklist: eight numbered rows, the tick and the document
     both left blank for whoever fills in the letter. */
  const checklistCell = (index: number) => [
    { text: String(index + 1), bold: true, alignment: "center", margin: [0, 7, 0, 7] },
    { text: "" },
    { text: "", fontSize: 8, margin: [6, 7.5, 4, 7] },
  ];

  const sorCell = (text: any, extra: any = {}) => ({ text: text ?? "", margin: [4, 5, 4, 5], ...extra });
  const sorHead = (text: string) => sorCell(text, { bold: true, color: WHITE, margin: [4, 7, 4, 7] });

  return {
    pageSize: "A4",
    pageOrientation: "portrait",
    pageMargins: [36, 134, 36, 30],
    defaultStyle: { fontSize: 9, color: "#1F1F1F", lineHeight: 1.05 },
    header: () => ({
      margin: [36, 22, 36, 0],
      stack: [
        {
          columns: [
            { image: mahbLogo, width: 120 },
            { text: "", width: "*" },
            {
              width: 165,
              stack: [
                { text: "MALAYSIA AIRPORTS HOLDINGS BERHAD", bold: true, fontSize: 8, margin: [0, 0, 0, 3] },
                ...ADDRESS.map((line) => ({ text: line, fontSize: 7.5, color: "#333333", margin: [0, 0, 0, 1.5] })),
                {
                  columns: [
                    { text: "Tel:03-8777 7000", fontSize: 7.5, color: "#333333" },
                    { text: "Fax: 603-8777 7778", fontSize: 7.5, color: "#333333" },
                  ],
                },
              ],
            },
          ],
        },
        { text: title, bold: true, fontSize: 11, color: TITLE, alignment: "center", margin: [0, 4, 0, 0] },
        rule(),
      ],
    }),
    content: [
      /* Page 1 — the letter. */
      {
        table: {
          widths: [80, 10, 175, 92, 10, "*"],
          body: [
            [...labelled("To", pick(detail?.vendorName, detail?.vendor)), ...labelled("Work Order Number", workOrderNo)],
            [...labelled("Date of Issuance", issueDate(pick(detail?.dateRequest, detail?.createdOn))), {}, {}, {}],
            [...labelled("Job Title", jobTitle, 4), {}, {}, {}],
          ],
        },
        layout: "noBorders",
        margin: [0, 0, 0, 8],
      },
      bar("Work Order Information"),
      {
        table: {
          widths: [130, 12, "*"],
          body: [
            /* The Subsidiary/Division — `division` on the record is the Cost Centre. */
            info(1, "Division", pick(detail?.subsidiary, detail?.subsidiaryName)),
            info(2, "Work Category", pick(detail?.contractCategoryName, detail?.categoryName, detail?.category)),
            info(3, "Region", pick(detail?.regionName, detail?.region)),
            info(4, "Work Order Sum", `RM${money(total)} (inclusive of SST)`),
            info(5, "Duration", durationOf(detail?.commencementDate, detail?.completionDate)),
            info(6, "Performance Bond", `RM${money(total * 0.05)} (5% of RM${money(total)})`),
            /* Left blank — filled in on the issued letter. */
            info(7, "Insurances", "", 8),
            info(8, "Liquidated Ascertained Damages (LAD)/ Penalty", ""),
            info(9, "Defects Liability Period (DLP)/ Warranty Period", ""),
          ],
        },
        layout: "noBorders",
        margin: [4, 4, 0, 0],
      },
      rule(),
      {
        columns: [
          { text: "DOCUMENT ATTACHED", bold: true, fontSize: 10, width: "auto" },
          { text: "( Tick (", fontSize: 7.5, width: "auto", margin: [4, 2, 0, 0] },
          { ...tick(7), width: 9, margin: [1, 3, 0, 0] },
          { text: ") If Any)", fontSize: 7.5, width: "auto", margin: [0, 2, 0, 0] },
        ],
        margin: [0, 0, 0, 6],
      },
      {
        table: {
          widths: [26, 26, "*", 26, 26, "*"],
          body: [0, 2, 4, 6].map((first) => [...checklistCell(first), ...checklistCell(first + 1)]),
        },
        layout: {
          hLineWidth: () => 0.8,
          vLineWidth: () => 0.8,
        },
        margin: [4, 0, 4, 0],
      },
      {
        text: "*This work order is to be read together with the document attached",
        bold: true,
        fontSize: 7.5,
        margin: [0, 6, 0, 0],
      },
      rule(),
      /* Acceptance and signatures stay together. */
      {
        unbreakable: true,
        stack: [
          bar("ACCEPTANCE"),
          {
            text: "We hereby acknowledge acceptance of this Work Order based on all of the terms, conditions and stipulations contained herein.",
            italics: true,
            margin: [6, 6, 0, 26],
          },
          {
            columns: [
              {
                width: 210,
                stack: [
                  dotted(200),
                  { text: "Signature of Tenderer :", margin: [0, 6, 0, 10] },
                  { text: "Name in full :", margin: [0, 0, 0, 10] },
                  { text: "In the capacity of :" },
                ],
              },
              { text: "", width: "*" },
              {
                width: 170,
                stack: [
                  dotted(160),
                  { text: "Company Stamp :", margin: [0, 6, 0, 10] },
                  { text: "Date :" },
                ],
              },
            ],
            margin: [6, 0, 0, 0],
          },
        ],
      },

      /* Page 2 on — the SOR schedule. */
      {
        pageBreak: "before",
        table: {
          widths: [50, 10, 200, 92, 10, "*"],
          body: [[...labelled("Job Title", jobTitle), ...labelled("Work Order Number", workOrderNo)]],
        },
        layout: "noBorders",
        margin: [0, 0, 0, 12],
      },
      {
        table: {
          headerRows: 1,
          dontBreakRows: true,
          widths: [46, "*", 56, 46, 60, 72],
          body: [
            ["SOR ID", "SOR Item", "Unit", "Qty", "Rate (RM)", "Amount (RM)"].map(sorHead),
            ...sor.map((row) => [
              sorCell(row.sorId),
              sorCell(pick(row.itemName) ?? "-"),
              sorCell(row.uom),
              sorCell(figure(row.quantity)),
              sorCell(figure(row.rate)),
              sorCell(figure(row.amount)),
            ]),
            [
              sorCell("Total Amount (Inclusive of SST) :", {
                colSpan: 5,
                alignment: "right",
                bold: true,
                italics: true,
                fontSize: 10,
                color: WHITE,
                margin: [4, 9, 8, 9],
              }),
              {},
              {},
              {},
              {},
              sorCell(money(total), { bold: true, fontSize: 10, color: WHITE, margin: [4, 9, 4, 9] }),
            ],
          ],
        },
        layout: {
          hLineWidth: () => 0.8,
          vLineWidth: () => 0.8,
          hLineColor: () => LINE,
          vLineColor: () => LINE,
          /* Navy heading and total, rows striped white / lavender. */
          fillColor: (row: number, node: any) =>
            row === 0 || row === node.table.body.length - 1 ? NAVY : row % 2 === 0 ? STRIPE : null,
        },
      },
    ],
  };
};

/** Builds the letter — "DRAFT WORK ORDER", or "WORK ORDER" once final — for viewing or saving. */
export const createDraftWorkOrderPdf = (detail: any, title?: string) =>
  pdfMake.createPdf(draftWorkOrderPdf(detail, title) as any);
